import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SimpleOnboardingModal = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    businessWebsite: '',
    industry: '',
    industryOther: '', // For custom industry input
    targetAudience: '',
    buyerRole: '',
    geographicFocus: '',
    geographicOther: '', // For custom geographic input
    solutionCategory: '',
    keyDifferentiators: '',
    competitors: '',
    businessStage: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Disable escape key - onboarding cannot be skipped
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Do nothing - onboarding is mandatory
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const validateUrl = (url) => {
    if (!url || !url.trim()) return false; // Required field
    
    try {
      // Add protocol if missing
      let validUrl = url.trim();
      if (!validUrl.match(/^https?:\/\//)) {
        validUrl = `https://${validUrl}`;
      }
      
      new URL(validUrl); // Validate URL format
      return validUrl;
    } catch {
      return false;
    }
  };

  const handleNextStep = () => {
    setError('');
    
    // Validate step 1
    if (currentStep === 1) {
      if (!formData.businessName.trim()) {
        setError('Business name is required');
        return;
      }
      if (formData.businessName.trim().length < 2) {
        setError('Business name must be at least 2 characters');
        return;
      }
      if (!formData.businessWebsite.trim()) {
        setError('Business website is required');
        return;
      }
      const validatedUrl = validateUrl(formData.businessWebsite);
      if (!validatedUrl) {
        setError('Please enter a valid website URL');
        return;
      }
      setFormData({ ...formData, businessWebsite: validatedUrl });
    }
    
    // Validate step 2
    if (currentStep === 2) {
      if (!formData.industry) {
        setError('Please select your industry');
        return;
      }
      if (formData.industry === 'Other' && !formData.industryOther.trim()) {
        setError('Please specify your industry');
        return;
      }
      if (!formData.targetAudience) {
        setError('Please select your target audience');
        return;
      }
      if (!formData.buyerRole) {
        setError('Please select the buyer role');
        return;
      }
      if (!formData.geographicFocus) {
        setError('Please select your geographic focus');
        return;
      }
      if ((formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') && !formData.geographicOther.trim()) {
        setError('Please specify your geographic location');
        return;
      }
    }
    
    // Validate step 3
    if (currentStep === 3) {
      if (!formData.solutionCategory.trim()) {
        setError('Solution category is required');
        return;
      }
      if (!formData.competitors.trim()) {
        setError('Please enter at least one competitor');
        return;
      }
      if (!formData.businessStage) {
        setError('Please select your business stage');
        return;
      }
    }
    
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);
    setIsGenerating(true);

    try {
      // First, submit the basic onboarding data
      const startResponse = await fetch(`${process.env.REACT_APP_API_URL}/onboarding/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessName: formData.businessName.trim(),
          businessWebsite: formData.businessWebsite
        })
      });

      if (!startResponse.ok) {
        const result = await startResponse.json();
        throw new Error(result.error || 'Failed to save business information');
      }

      // Prepare the context data, using custom values if "Other" or "Specific" was selected
      const contextData = {
        industry: formData.industry === 'Other' ? formData.industryOther.trim() : formData.industry,
        targetAudience: formData.targetAudience,
        buyerRole: formData.buyerRole,
        geographicFocus: (formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') 
          ? formData.geographicOther.trim() 
          : formData.geographicFocus,
        solutionCategory: formData.solutionCategory.trim(),
        keyDifferentiators: formData.keyDifferentiators.trim() || null,
        competitors: formData.competitors.trim(),
        businessStage: formData.businessStage // This will now have the correct snake_case value
      };

      // Then submit the context data which will trigger query generation
      const contextResponse = await fetch(`${process.env.REACT_APP_API_URL}/onboarding/context`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contextData)
      });

      const contextResult = await contextResponse.json();

      if (!contextResponse.ok) {
        throw new Error(contextResult.error || 'Failed to generate queries');
      }

      // Success!
      setIsSuccess(true);
      updateUser({ onboardingCompleted: true });
      
      // Show success message briefly then redirect
      setTimeout(() => {
        onClose();
        navigate('/dashboard');
      }, 3000);

    } catch (error) {
      setError(error.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-md flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // Prevent closing by clicking backdrop - onboarding is mandatory
        e.stopPropagation();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="relative max-w-lg w-full mx-auto"
      >
        <div className="glass-modal rounded-3xl p-6 sm:p-8 relative overflow-hidden max-h-[90vh] overflow-y-auto">
          {/* Decorative gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent-400/10 to-transparent pointer-events-none" />
          
          {/* Close button removed - onboarding is mandatory */}
          
          {/* Progress indicator */}
          {!isSuccess && !isGenerating && (
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    step === currentStep 
                      ? 'w-8 bg-accent-400' 
                      : step < currentStep 
                        ? 'w-2 bg-accent-400/50' 
                        : 'w-2 bg-gray-600'
                  }`}
                />
              ))}
            </div>
          )}
          
          <AnimatePresence mode="wait">
            {isGenerating ? (
              <motion.div
                key="generating"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="glass-spinner w-12 h-12 mx-auto mb-6"></div>
                
                <h3 className={`heading-mono text-xl mb-2 ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}>
                  Generating Your Personalized Queries
                </h3>
                
                <p className={`text-mono text-sm mb-4 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  AI is analyzing your business context to create 25 targeted search queries...
                </p>
                
                <div className={`rounded-lg p-4 mb-4 ${
                  isDark ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`text-mono text-xs ${isDark ? 'text-blue-200' : 'text-blue-600'}`}>
                    This may take 30-60 seconds as we generate queries optimized for your:
                  </p>
                  <ul className={`text-mono text-xs mt-2 space-y-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    <li>• {formData.industry === 'Other' ? formData.industryOther : formData.industry} industry</li>
                    <li>• {formData.targetAudience} market</li>
                    <li>• {(formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') ? formData.geographicOther : formData.geographicFocus} region</li>
                  </ul>
                </div>
              </motion.div>
            ) : isSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="w-20 h-20 mx-auto mb-4 glass-accent rounded-full flex items-center justify-center"
                >
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                
                <h3 className={`heading-mono text-xl mb-2 ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}>
                  Welcome aboard!
                </h3>
                
                <p className={`text-mono mb-4 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Your 25 personalized queries are ready
                </p>
                
                <div className={`rounded-lg p-4 ${
                  isDark ? 'bg-green-900/20 border border-green-700/30' : 'bg-green-50 border border-green-200'
                }`}>
                  <p className={`text-mono text-xs ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                    ✅ Created 5 query groups based on your business context
                  </p>
                </div>
              </motion.div>
            ) : (
              <>
                {/* Step 1: Business Information */}
                {currentStep === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className={`heading-mono text-xl sm:text-2xl mb-4 text-center ${
                      isDark ? 'text-white' : 'text-gray-800'
                    }`}>
                      Welcome! Let's get started
                    </h2>

                    {/* Explanation box */}
                    <div className={`rounded-lg p-4 mb-6 ${
                      isDark ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isDark ? 'text-blue-400' : 'text-blue-600'
                        }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-mono text-xs space-y-2">
                          <p className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                            3 quick steps to get started:
                          </p>
                          <ul className={`space-y-1 ${isDark ? 'text-blue-200' : 'text-blue-600'}`}>
                            <li>1. Enter your business details</li>
                            <li>2. Tell us about your market</li>
                            <li>3. Define your competitive landscape</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Business Name */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          What's your business name? *
                        </label>
                        <input
                          type="text"
                          value={formData.businessName}
                          onChange={(e) => {
                            setFormData({ ...formData, businessName: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none"
                          placeholder="Enter your business name"
                          disabled={isLoading}
                          autoFocus
                        />
                      </div>

                      {/* Business Website */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Business website *
                        </label>
                        <input
                          type="text"
                          value={formData.businessWebsite}
                          onChange={(e) => {
                            setFormData({ ...formData, businessWebsite: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none"
                          placeholder="example.com"
                          disabled={isLoading}
                        />
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-mono text-sm text-center"
                        >
                          {error}
                        </motion.div>
                      )}

                      {/* Next button */}
                      <button
                        onClick={handleNextStep}
                        disabled={isLoading}
                        className="w-full btn-glass-accent px-6 py-3 rounded-xl text-mono font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next →
                      </button>

                      {/* Skip link removed - onboarding is mandatory */}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Market Context */}
                {currentStep === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className={`heading-mono text-xl sm:text-2xl mb-6 text-center ${
                      isDark ? 'text-white' : 'text-gray-800'
                    }`}>
                      Tell us about your market
                    </h2>

                    <div className="space-y-4">
                      {/* Industry */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Industry *
                        </label>
                        <select
                          value={formData.industry}
                          onChange={(e) => {
                            setFormData({ ...formData, industry: e.target.value, industryOther: '' });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono focus:outline-none"
                        >
                          <option value="">Select your industry</option>
                          <option value="SaaS">SaaS</option>
                          <option value="E-commerce">E-commerce</option>
                          <option value="Healthcare">Healthcare</option>
                          <option value="Finance">Finance</option>
                          <option value="Cybersecurity">Cybersecurity</option>
                          <option value="Marketing/Advertising">Marketing/Advertising</option>
                          <option value="Education">Education</option>
                          <option value="Manufacturing">Manufacturing</option>
                          <option value="Other">Other</option>
                        </select>
                        
                        {/* Show text input if Other is selected */}
                        {formData.industry === 'Other' && (
                          <input
                            type="text"
                            value={formData.industryOther}
                            onChange={(e) => {
                              setFormData({ ...formData, industryOther: e.target.value });
                              setError('');
                            }}
                            className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none mt-2"
                            placeholder="Please specify your industry"
                          />
                        )}
                      </div>

                      {/* Target Audience */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Target Audience *
                        </label>
                        <select
                          value={formData.targetAudience}
                          onChange={(e) => {
                            setFormData({ ...formData, targetAudience: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono focus:outline-none"
                        >
                          <option value="">Select target audience</option>
                          <option value="Enterprise (Fortune 500)">Enterprise (Fortune 500)</option>
                          <option value="Mid-market">Mid-market</option>
                          <option value="SMB (Small/Medium Business)">SMB (Small/Medium Business)</option>
                          <option value="Startups">Startups</option>
                          <option value="Consumers">Consumers</option>
                          <option value="Government">Government</option>
                          <option value="Non-profit">Non-profit</option>
                        </select>
                      </div>

                      {/* Buyer Role */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Primary Buyer Role *
                        </label>
                        <select
                          value={formData.buyerRole}
                          onChange={(e) => {
                            setFormData({ ...formData, buyerRole: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono focus:outline-none"
                        >
                          <option value="">Select buyer role</option>
                          <option value="C-Suite/Executive">C-Suite/Executive</option>
                          <option value="IT Decision Makers">IT Decision Makers</option>
                          <option value="Marketing Leaders">Marketing Leaders</option>
                          <option value="Product Managers">Product Managers</option>
                          <option value="Developers/Engineers">Developers/Engineers</option>
                          <option value="End Users">End Users</option>
                          <option value="Procurement Teams">Procurement Teams</option>
                        </select>
                      </div>

                      {/* Geographic Focus */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Geographic Focus *
                        </label>
                        <select
                          value={formData.geographicFocus}
                          onChange={(e) => {
                            setFormData({ ...formData, geographicFocus: e.target.value, geographicOther: '' });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono focus:outline-none"
                        >
                          <option value="">Select geographic focus</option>
                          <option value="Global">Global</option>
                          <option value="North America">North America</option>
                          <option value="Europe">Europe</option>
                          <option value="Asia Pacific">Asia Pacific</option>
                          <option value="Latin America">Latin America</option>
                          <option value="Middle East & Africa">Middle East & Africa</option>
                          <option value="Specific Country">Specific Country</option>
                          <option value="Specific State/Region">Specific State/Region</option>
                        </select>
                        
                        {/* Show text input if Specific Country or State/Region is selected */}
                        {(formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') && (
                          <input
                            type="text"
                            value={formData.geographicOther}
                            onChange={(e) => {
                              setFormData({ ...formData, geographicOther: e.target.value });
                              setError('');
                            }}
                            className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none mt-2"
                            placeholder={formData.geographicFocus === 'Specific Country' ? 'e.g., United States, Germany, Japan' : 'e.g., California, Texas, Ontario'}
                          />
                        )}
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-mono text-sm text-center"
                        >
                          {error}
                        </motion.div>
                      )}

                      {/* Navigation buttons */}
                      <div className="flex gap-3">
                        <button
                          onClick={handlePreviousStep}
                          className="flex-1 btn-glass px-6 py-3 rounded-xl text-mono font-medium"
                        >
                          ← Back
                        </button>
                        <button
                          onClick={handleNextStep}
                          className="flex-1 btn-glass-accent px-6 py-3 rounded-xl text-mono font-medium"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Competitive Landscape */}
                {currentStep === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className={`heading-mono text-xl sm:text-2xl mb-6 text-center ${
                      isDark ? 'text-white' : 'text-gray-800'
                    }`}>
                      Define your competitive landscape
                    </h2>

                    <div className="space-y-4">
                      {/* Solution Category */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Solution Category *
                        </label>
                        <input
                          type="text"
                          value={formData.solutionCategory}
                          onChange={(e) => {
                            setFormData({ ...formData, solutionCategory: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none"
                          placeholder="e.g., CRM software, project management tool"
                        />
                      </div>

                      {/* Key Differentiators */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Key Differentiators (optional)
                        </label>
                        <textarea
                          value={formData.keyDifferentiators}
                          onChange={(e) => {
                            setFormData({ ...formData, keyDifferentiators: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none resize-none"
                          placeholder="What makes your solution unique?"
                          rows="3"
                        />
                      </div>

                      {/* Competitors */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Main Competitors *
                        </label>
                        <input
                          type="text"
                          value={formData.competitors}
                          onChange={(e) => {
                            setFormData({ ...formData, competitors: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 glass-input rounded-xl text-mono placeholder-gray-400 focus:outline-none"
                          placeholder="e.g., Salesforce, HubSpot, Pipedrive"
                        />
                        <p className={`text-mono text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Separate multiple competitors with commas
                        </p>
                      </div>

                      {/* Business Stage */}
                      <div>
                        <label className={`block text-mono text-sm mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Business Stage *
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'entering_new_market', label: 'Entering new market' },
                            { value: 'defending_position', label: 'Defending market position' },
                            { value: 'category_creation', label: 'Category creation' },
                            { value: 'growing_market_share', label: 'Growing market share' }
                          ].map((option) => (
                            <label key={option.value} className="flex items-center">
                              <input
                                type="radio"
                                name="businessStage"
                                value={option.value}
                                checked={formData.businessStage === option.value}
                                onChange={(e) => {
                                  setFormData({ ...formData, businessStage: e.target.value });
                                  setError('');
                                }}
                                className="mr-3"
                              />
                              <span className={`text-mono text-sm ${
                                isDark ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-mono text-sm text-center"
                        >
                          {error}
                        </motion.div>
                      )}

                      {/* Navigation buttons */}
                      <div className="flex gap-3">
                        <button
                          onClick={handlePreviousStep}
                          disabled={isLoading}
                          className="flex-1 btn-glass px-6 py-3 rounded-xl text-mono font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ← Back
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={isLoading}
                          className="flex-1 btn-glass-accent px-6 py-3 rounded-xl text-mono font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Generating...
                            </span>
                          ) : (
                            'Generate My Queries'
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SimpleOnboardingModal;